import { useState, type DragEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import ImageIcon from "react-feather/dist/icons/image";
import AlertCircle from "react-feather/dist/icons/alert-circle";

import style from "./style.module.scss";
import WizardFooter from "../wizard-footer";
import PageContent from "../page-content";
import ImageDropZone from "../../../media/components/image-drop-zone";
import { requireLogin } from "../../../security";
import { productQueryOptions } from "../../../data/queries/products";
import { mediaByProductQueryOptions, type Media } from "../../../data/queries/media";
import { useUser } from "../../../data/context/auth-context";
import { useUploadWithProgress, type UploadItem } from "../../../hooks";
import { getProductType } from "../../../product";
import { Button, FileButton, Spinner } from "../../../ui";
import { trackUploadComplete } from "../../../analytics";

interface PhotoProgressProps {
  photoCount: number;
  recommended: number;
}

function PhotoProgress({ photoCount, recommended }: PhotoProgressProps) {
  const progress = Math.min(100, (photoCount / recommended) * 100);

  return (
    <div className={style.progress}>
      <span className={style.progressCount}>
        {photoCount} / {recommended} photos
      </span>
      <div className={style.progressBar}>
        <div className={style.progressFill} style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}

interface PhotoItemProps {
  image?: Media;
  upload?: UploadItem;
}

function PhotoItem({ image, upload }: PhotoItemProps) {
  const isUploading = upload?.status === "uploading" || upload?.status === "pending";
  const isError = upload?.status === "error";
  const imageUrl = upload?.previewUrl || image?.url;

  return (
    <div className={style.photoItem}>
      {imageUrl && <img src={imageUrl} alt="" className={style.photoImage} />}
      {isUploading && <ImageDropZone loading dark progress={upload.progress || 0} />}
      {isError && (
        <div className={style.errorOverlay} title="Cliquez pour réessayer">
          <AlertCircle className={style.errorIcon} />
          <span className={style.errorText}>Erreur</span>
        </div>
      )}
    </div>
  );
}

interface LargeDropZoneProps {
  onSelectFiles: (files: File[]) => void;
}

function LargeDropZone({ onSelectFiles }: LargeDropZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (e.dataTransfer.files?.length > 0) {
      const imageFiles = [...e.dataTransfer.files].filter((file) => file.type.startsWith("image/"));
      if (imageFiles.length > 0) {
        onSelectFiles(imageFiles);
      }
    }
  };

  return (
    <FileButton
      accept="image/*"
      multiple
      onSelect={onSelectFiles}
      render={({ onClick }) => (
        <div
          className={`${style.dropZone} ${isDragOver ? style.dropZoneDragOver : ""}`}
          onClick={onClick}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <ImageIcon className={style.dropZoneIcon} />
          <h3 className={style.dropZoneTitle}>Ajoutez vos photos</h3>
          <p className={style.dropZoneText}>Cliquez ou glissez-déposez vos photos ici</p>
        </div>
      )}
    />
  );
}

interface PhotoGridProps {
  media: Media[];
  uploads: Record<string, UploadItem>;
  onSelectFiles: (files: File[]) => void;
}

function PhotoGrid({ media, uploads, onSelectFiles }: PhotoGridProps) {
  const activeUploads = Object.values(uploads);

  const hasPhotos = media.length > 0 || activeUploads.length > 0;

  if (!hasPhotos) {
    return <LargeDropZone onSelectFiles={onSelectFiles} />;
  }

  return (
    <div className={style.photoGrid}>
      {/* Existing uploaded photos */}
      {media.map((image) => (
        <PhotoItem key={image.uid} image={image} />
      ))}

      {/* Currently uploading photos */}
      {activeUploads.map((upload) => (
        <PhotoItem key={upload.id} upload={upload} />
      ))}

      {/* Upload button */}
      <FileButton
        accept="image/*"
        multiple
        onSelect={onSelectFiles}
        render={({ onClick }) => (
          <div className={style.uploadButton} onClick={onClick}>
            <ImageDropZone className={style.uploadButtonInner} onDrop={onSelectFiles} />
          </div>
        )}
      />
    </div>
  );
}

function Upload() {
  const { uid } = useParams();
  const user = useUser();

  const { data: product, isLoading: isLoadingProduct } = useQuery(productQueryOptions(uid || ""));
  const { data: media = [] } = useQuery(mediaByProductQueryOptions(uid || "", user?.uid));

  const { uploads, uploadFiles, isUploading } = useUploadWithProgress({
    productId: uid || "",
    userId: user?.uid || "",
  });

  const handleSelectFiles = (files: File[]) => {
    uploadFiles(files);
  };

  if (isLoadingProduct || !product) {
    return <Spinner />;
  }

  const totalPhotos = media.length;
  const productType = getProductType(product.name);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recommendation = productType?.photoRecommendation?.(product.options as any);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isReady = productType?.isReady(product.options as any) ?? false;

  return (
    <>
      <PageContent
        title="Ajoutez vos photos"
        description={recommendation?.message || "Sélectionnez les photos pour votre produit."}
      >
        {recommendation && (
          <PhotoProgress photoCount={totalPhotos} recommended={recommendation.recommended} />
        )}
        <PhotoGrid media={media} uploads={uploads} onSelectFiles={handleSelectFiles} />
      </PageContent>

      <WizardFooter uid={uid} step={1} enabledSteps={isReady ? [1, 2, 3, 4] : [1, 2, 3]}>
        {isUploading && <span className={style.uploadingStatus}>Chargement en cours...</span>}
        <span className={style.photoCount}>{totalPhotos} photos</span>
        <Link
          to={`/settings/${uid}`}
          onClick={() => trackUploadComplete(product.name, totalPhotos)}
        >
          <Button isPrimary>Continuer</Button>
        </Link>
      </WizardFooter>
    </>
  );
}

export default requireLogin()(Upload);
